/**
 * useLiveMapProvider — lifecycle binding for the MapProvider port
 * (ODE-P07-T02, REQ-084).
 *
 * Deterministic + fail-safe responsibilities:
 *   - when the map body is shown (ready state + a `provider` resolution),
 *     build a FRESH adapter instance through the factory (never reused
 *     across cycles), mount it into the canvas container, and destroy it on
 *     teardown — reconnect/reload cycles always get a clean provider;
 *   - push derived markers on every snapshot update (last known fixes
 *     exactly as the server reported them — the hook never computes
 *     positions itself);
 *   - surface adapter construction/load/runtime failures as a REQ-090
 *     error message instead of crashing.
 *
 * Traces to: ODE-PRD-REQ-084, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createMapProviderHandle } from './provider-registry.js';
import type { MapMarkerView, MapProvider, MapProviderResolution } from './map-provider.js';
import type { GoogleMapsLoader } from './google-adapter.js';

export interface UseLiveMapProviderOptions {
  /** Adapter factory seam (tests inject a tracked FakeMapAdapter factory). */
  readonly createProvider?: (resolution: MapProviderResolution) => MapProvider;
  /** Google adapter loader seam (tests inject a fake loader). */
  readonly googleLoader?: GoogleMapsLoader;
}

export interface UseLiveMapProviderResult {
  /** Callback ref for the container the provider mounts into. */
  readonly containerRef: (node: HTMLDivElement | null) => void;
  /** Adapter failure message (rendered as the REQ-090 error state). */
  readonly mapError: string | null;
  /** True while an adapter instance is mounted. */
  readonly mounted: boolean;
}

/** Semantic identity of a resolution (stable across object re-creations). */
function resolutionKey(resolution: MapProviderResolution): string {
  switch (resolution.kind) {
    case 'fake':
      return 'fake';
    case 'google':
      return `google:${resolution.apiKey}`;
    case 'misconfigured':
      return `misconfigured:${resolution.reason}`;
  }
}

export function useLiveMapProvider(
  resolution: MapProviderResolution,
  markers: readonly MapMarkerView[],
  enabled: boolean,
  options: UseLiveMapProviderOptions = {},
): UseLiveMapProviderResult {
  const containerRefHolder = useRef<HTMLDivElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const providerRef = useRef<MapProvider | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    containerRefHolder.current = node;
    setContainer(node);
  }, []);

  // Seams live in refs so the effects depend only on the lifecycle inputs.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const resolutionRef = useRef(resolution);
  resolutionRef.current = resolution;

  // The lifecycle binds on the SEMANTIC resolution key (kind + key/reason),
  // not object identity — defensively-stable across equal-but-recreated
  // resolution objects (inline literals in callers never rebind the map).
  const key = resolutionKey(resolution);

  useEffect(() => {
    if (!enabled || resolution.kind === 'misconfigured' || container === null) {
      return undefined;
    }

    const setError = (message: string): void => {
      setMapError(message);
    };

    let provider: MapProvider;
    try {
      const currentResolution = resolutionRef.current;
      const createProvider =
        optionsRef.current.createProvider ??
        ((res: MapProviderResolution): MapProvider => {
          const handle = createMapProviderHandle(res, {
            ...(optionsRef.current.googleLoader !== undefined
              ? { loader: optionsRef.current.googleLoader }
              : {}),
            onError: setError,
          });
          if (handle.kind !== 'provider') {
            throw new Error('invalid provider resolution');
          }
          return handle.provider;
        });
      provider = createProvider(currentResolution);
    } catch (error: unknown) {
      setMapError(error instanceof Error ? error.message : 'Gagal menyiapkan peta.');
      return undefined;
    }

    try {
      provider.mount(container);
    } catch (error: unknown) {
      setMapError(error instanceof Error ? error.message : 'Gagal memuat peta.');
      return undefined;
    }
    providerRef.current = provider;
    setMounted(true);
    setMapError(null);
    // Markers are pushed by the dedicated effect below (it always runs
    // right after this effect whenever this lifecycle effect re-runs),
    // so a (re)mount renders the latest snapshot markers exactly once.
    return () => {
      providerRef.current = null;
      setMounted(false);
      try {
        provider.destroy();
      } catch {
        // An adapter teardown failure never prevents cleanup.
      }
    };
    // Markers are pushed via the dedicated effect below; this lifecycle
    // effect intentionally rebinds only on key/enabled/container changes.
  }, [key, enabled, container]);

  // Marker pushes on every snapshot update while the map is shown.
  useEffect(() => {
    if (!enabled || container === null) return;
    const provider = providerRef.current;
    if (provider === null) return;
    provider.setMarkers(markers);
  }, [enabled, container, markers, key]);

  return { containerRef, mapError, mounted };
}
